<template>
  <div>
    <!-- Loading State -->
    <empty-state
      v-if="loading"
      :title="$t('loading')"
      :loading="true"
    />

    <!-- Empty State -->
    <empty-state
      v-else-if="meals.length === 0"
      icon="mdi-food-off"
      :title="$t('no_reserved_meals_data')"
      :subtitle="$t('select_date_and_company')"
    />

    <template v-else>
      <v-data-table
        :headers="headers"
        :items="customSearch(meals, headers, search)"
        hide-default-footer
        disable-pagination
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:item.num="{ index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.name="{ item }">
          {{ toLatin(item.name) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-account-group"
            color="primary"
            :tooltip="$t('show_employees_who_ordered')"
            @click="openEmployeesDialog(item)"
          />
        </template>
        <template v-slot:body.append>
          <tr class="font-weight-bold">
            <td></td>
            <td>{{ $t("total") }}</td>
            <td>{{ totals.shift_one }}</td>
            <td>{{ totals.shift_two }}</td>
            <td>{{ totals.shift_three }}</td>
            <td>{{ totals.shift_four }}</td>
            <td>{{ totals.total }}</td>
            <td></td>
          </tr>
        </template>
      </v-data-table>
    </template>

    <employees-who-ordered-dialog
      v-model="employeesDialog"
      :selected-meal="selectedMeal"
      :selected-company="selectedCompany"
      :filters="filters"
    />
  </div>
</template>

<script>
import reportService from "@/services/report";
import EmployeesWhoOrderedDialog from "@/components/report/meals/dialogs/EmployeesWhoOrderedDialog.vue";
import EmptyState from "@/components/global/EmptyState.vue";

export default {
  name: "CompanyReportTabReservedMeals",
  components: {
    EmployeesWhoOrderedDialog,
    EmptyState,
  },
  data: () => ({
    report: null,
    loading: false,
    employeesDialog: false,
    selectedMeal: null,
  }),
  created() {
    this.fetchReservedMeals();
  },
  methods: {
    async fetchReservedMeals() {
      this.loading = true;

      const params = {
        date: this.dates.from,
        date2: this.dates.to,
      };

      try {
        const response = await reportService.getReservedMeals(this.restaurantId, params);
        this.report = response.data;
      } catch (error) {
        console.error("Error fetching reserved meals:", error);
      } finally {
        this.loading = false;
      }
    },
    openEmployeesDialog(meal) {
      this.selectedMeal = meal;
      this.employeesDialog = true;
    },
  },
  computed: {
    search() {
      return this.$store.state.report.filters.search;
    },
    filters() {
      return this.$store.state.report.filters;
    },
    selectedCompany() {
      // Get the first selected company for the dialog
      const companies = this.$store.state.report.selectedFoodCompanies || [];
      return companies.length > 0 ? companies[0] : null;
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies
        .map((company) => company.id)
        .join(",");
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    restaurantId() {
      return this.$store.state.company?.restaurant_id || this.$store.state.company;
    },
    meals() {
      if (!this.report) return [];

      const foods = {};
      const selectedCompanyNames = (
        this.$store.state.report.selectedFoodCompanies || []
      ).map((c) => c.name);

      Object.entries(this.report).forEach(([companyName, shifts]) => {
        // Filter by selected companies
        if (
          selectedCompanyNames.length > 0 &&
          !selectedCompanyNames.includes(companyName)
        ) {
          return;
        }

        // Process each shift
        Object.entries(shifts).forEach(([shiftKey, meals]) => {
          const shiftField =
            shiftKey === "1-smena"
              ? "shift_one"
              : shiftKey === "2-smena"
              ? "shift_two"
              : shiftKey === "3-smena"
              ? "shift_three"
              : shiftKey === "4-smena"
              ? "shift_four"
              : null;

          if (!shiftField) return;

          Object.entries(meals).forEach(([mealName, mealData]) => {
            if (!foods[mealName]) {
              foods[mealName] = {
                id: mealData.id,
                name: mealName,
                shift_one: 0,
                shift_two: 0,
                shift_three: 0,
                shift_four: 0,
                total: 0,
              };
            }
            foods[mealName][shiftField] += mealData.quantity;
            foods[mealName].total += mealData.quantity;
          });
        });
      });

      return Object.values(foods);
    },
    totals() {
      const totals = {
        shift_one: 0,
        shift_two: 0,
        shift_three: 0,
        shift_four: 0,
        total: 0,
      };

      this.meals.forEach((meal) => {
        totals.shift_one += meal.shift_one || 0;
        totals.shift_two += meal.shift_two || 0;
        totals.shift_three += meal.shift_three || 0;
        totals.shift_four += meal.shift_four || 0;
        totals.total += meal.total || 0;
      });

      return totals;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meal"), value: "name" },
        { text: this.$t("1st_shift"), value: "shift_one" },
        { text: this.$t("2nd_shift"), value: "shift_two" },
        { text: this.$t("3rd_shift"), value: "shift_three" },
        { text: this.$t("4th_shift"), value: "shift_four" },
        { text: this.$t("total"), value: "total" },
        { text: this.$t("actions"), value: "actions", sortable: false },
      ];
    },
  },
  watch: {
    "dates.from"() {
      this.fetchReservedMeals();
    },
    selectedCompanies() {
      this.fetchReservedMeals();
    },
  },
};
</script>
